import React, { useEffect } from 'react';
import { MerchantInfo } from 'store/termsAndConditions/termsAndConditionsSlice';
import { TermsTextWrapper, TermsPlainTextWrapper, TermsSubTitle, TermsPlainText } from 'pages/styles';

export const LoyaltyPrivacyPolicyFR = (merchantInfo: MerchantInfo) => {
  const { merchantName, termsUrl } = merchantInfo;

  const title: string = 'Global Payments POS';

  useEffect(() => {
    document.title = title;
  }, []);

  return (
    <TermsTextWrapper>
      <TermsPlainTextWrapper>
        <TermsSubTitle style={{ fontSize: 20, fontWeight: 'bold' }}>{title}</TermsSubTitle>
        <TermsSubTitle style={{ fontSize: 18, fontWeight: 'bold' }}>
          Avis de confidentialité concernant les programmes de fidélité et le marketing par courriel
        </TermsSubTitle>
        <TermsSubTitle style={{ fontWeight: 'bold' }}>Dernière mise à jour : 25 juillet 2025</TermsSubTitle>
        <TermsPlainText>
          Chez Global Payments, nous respectons vos préoccupations en matière de protection de la vie privée. Le présent
          Avis de confidentialité («Avis») décrit comment Global Payments Inc. ainsi que ses filiales et sociétés
          affiliées (désignées collectivement aux fins de simplification dans le présent Avis comme «Global Payments»,
          «nous» ou «notre») recueillent, utilisent et gèrent les renseignements personnels que nous collectons dans le
          cadre de la prestation des Services (définis ci-dessous). Veuillez lire cet Avis attentivement. Pour toute
          question, vous pouvez communiquer avec nous à l&apos;adresse courriel privacy@globalpay.com ou en utilisant
          les méthodes indiquées dans la section «Nous joindre» ci-dessous.
        </TermsPlainText>
        <TermsPlainText>
          Les produits et services de Global Payments comprennent des solutions de point de vente (PDV) qui permettent à
          nos clients commerciaux (chacun un «Marchand», collectivement les «Marchands») de traiter les paiements par
          carte de crédit et de débit ainsi que d&apos;effectuer diverses fonctions administratives. Dans le cadre de
          ces solutions de traitement des paiements, notre système PDV comprend des services conçus pour aider les
          Marchands à offrir à leurs clients (les «Clients») ce qui suit: (1) des programmes de fidélisation et
          d&apos;autres programmes d&apos;avantages (un «Programme de fidélité») et (2) des services de marketing par
          courriel (une «Campagne de marketing par courriel»). Les Programmes de fidélité et les Campagnes de marketing
          par courriel peuvent être désignés collectivement comme les «Services» dans le présent Avis, selon le
          contexte.{' '}
        </TermsPlainText>
        <TermsPlainText>
          En tant que Client de {merchantName || '[organisation name]'}, l&apos;un de nos Marchands, le présent Avis
          s&apos;applique à votre utilisation d&apos;un ou de plusieurs Services par l&apos;intermédiaire du système PDV
          de Global Payments. Cet Avis concerne uniquement les renseignements personnels traités dans le cadre des
          Services et constitue un complément à tout autre avis de confidentialité applicable fourni aux Marchands et/ou
          aux Clients.
        </TermsPlainText>
        <TermsPlainText>
          Étant donné que notre relation avec les Clients est indirecte, nous traitons leurs renseignements personnels
          uniquement dans le but de fournir les Services, conformément aux directives de nos Marchands. Les Marchands
          sont responsables de l&apos;établissement des <a href={termsUrl}>modalités de leur Programme de fidélité</a>{' '}
          et des paramètres de leurs Campagnes de marketing par courriel. Nos Marchands établissent également leurs
          propres pratiques commerciales et politiques en matière de protection des données et possèdent leurs propres
          avis de confidentialité. Si vous avez des questions sur la façon dont un Marchand recueille, utilise ou
          communique vos renseignements personnels, ou si vous souhaitez exercer vos droits à l&apos;égard de ces
          renseignements, veuillez contacter directement le Marchand.
        </TermsPlainText>
        <TermsPlainText>
          En utilisant les Services et/ou en nous fournissant vos renseignements personnels dans le cadre des Services,
          vous reconnaissez que vos renseignements personnels seront traités et utilisés de la manière décrite dans le
          présent Avis.
        </TermsPlainText>
        <TermsPlainText>Le présent Avis traite des sujets suivants:</TermsPlainText>

        <ul>
          <li>
            <a href={'#personalInfo'}>Renseignements personnels que nous recueillons</a>
          </li>
          <li>
            <a href={'#sourcesOfPI'}>Sources des renseignements personnels</a>
          </li>
          <li>
            <a href={'#howUsePI'}>Comment nous utilisons les renseignements personnels que nous recueillons</a>
          </li>
          <li>
            <a href={'#disclosePI'}>Comment nous divulguons les renseignements personnels que nous recueillons</a>
          </li>
          <li>
            <a href={'#affiliateCompanies'}>Notre relation avec les sociétés affiliées</a>
          </li>
          <li>
            <a href={'#financial'}>Avis concernant les incitatifs financiers / Programme de fidélité légitime</a>
          </li>
          <li>
            <a href={'#managingPreferences'}>Gestion de vos préférences</a>
          </li>
          <li>
            <a href={'#legalRights'}>Vos droits légaux</a>
          </li>
          <li>
            <a href={'#dataSecurity'}>Sécurité des données et conservation des données</a>
          </li>
          <li>
            <a href={'#cookies'}>Témoins (cookies) et autres technologies de suivi</a>
          </li>
          <li>
            <a href={'#externalLinks'}>Liens externes</a>
          </li>
          <li>
            <a href={'#children'}>Enfants</a>
          </li>
          <li>
            <a href={'#changesAndUpdates'}>Modifications et mises à jour</a>
          </li>
          <li>
            <a href={'#contactUs'}>Nous joindre</a>
          </li>
        </ul>

        <TermsSubTitle>
          <span id="personalInfo">Renseignements personnels que nous recueillons</span>
        </TermsSubTitle>
        <TermsPlainText>
          Les «renseignements personnels» sont des renseignements qui permettent de vous identifier en tant
          qu&apos;individu ou qui se rapportent à une personne identifiable. Sous réserve de votre consentement lorsque
          requis par la loi, nous pouvons recueillir des renseignements personnels auprès des Clients dans le cadre des
          Services, pour le compte de nos Marchands et conformément à leurs directives.
        </TermsPlainText>

        <TermsPlainText>
          Si vous choisissez de vous inscrire à un Programme de fidélité, nous pouvons recueillir, pour le compte de nos
          Marchands, les types de renseignements personnels suivants vous concernant:
        </TermsPlainText>

        <ul>
          <li>
            <b>Renseignements d&apos;identification de base</b>, y compris votre nom complet, votre numéro de téléphone
            et votre adresse courriel.
          </li>
          <li>
            <b>Renseignements de paiement</b>, y compris les numéros de cartes de crédit ou de débit.
          </li>
          <li>
            <b>Renseignements commerciaux</b>, y compris les enregistrements de vos transactions avec le(s) Marchand(s).
          </li>
          <li>
            <b>Renseignements que vous fournissez</b>, y compris le contenu de vos communications avec nous et les
            métadonnées associées.
          </li>
          <li>
            <b>Déductions</b> tirées des renseignements ci-dessus, telles que des inférences concernant vos préférences
            en matière de produits, vos caractéristiques ou vos prédispositions.
          </li>
        </ul>

        <TermsPlainText>
          Si vous choisissez de vous abonner à une Campagne de marketing par courriel, nous pouvons recueillir, pour le
          compte de nos Marchands, les types de renseignements personnels suivants vous concernant:
        </TermsPlainText>

        <ul>
          <li>
            <b>Renseignements d&apos;identification de base</b>, y compris votre nom complet, votre numéro de téléphone
            et votre adresse courriel.
          </li>
          <li>
            <b>Renseignements commerciaux</b>, y compris les enregistrements de vos transactions avec le(s) Marchand(s)
            et des renseignements sur vos préférences concernant les produits et services de nos Marchands qui
            pourraient vous intéresser.
          </li>
          <li>
            <b>Renseignements sur l&apos;appareil et autres identifiants uniques</b>, y compris l&apos;identifiant de
            l&apos;appareil, l&apos;adresse de protocole Internet (IP), les témoins (cookies), les pixels espions (pixel
            tags), l&apos;identifiant publicitaire mobile ou d&apos;autres identifiants uniques similaires.
          </li>
          <li>
            <b>Activité Internet ou autre activité sur le réseau</b>, y compris l&apos;historique de navigation ou de
            recherche et les renseignements concernant vos interactions avec une Campagne de marketing par courriel.
          </li>
          <li>
            <b>Renseignements que vous fournissez</b>, y compris le contenu de vos communications avec nous et les
            métadonnées associées.
          </li>
          <li>
            <b>Déductions tirées des renseignements ci-dessus</b> telles que des inférences concernant vos préférences
            en matière de produits, vos caractéristiques ou vos prédispositions.
          </li>
        </ul>

        <TermsPlainText>
          <u>Anonymisation et agrégation des données</u>. Sous réserve de votre consentement lorsque requis par la loi,
          nous pouvons anonymiser ou agréger les renseignements personnels vous concernant de manière à ce que vous ne
          soyez pas identifié ou identifiable à partir de ces données, afin d&apos;utiliser les données anonymisées ou
          agrégées, par exemple, à des fins d&apos;analyse statistique et de gestion, y compris l&apos;analyse des
          tendances ou de l&apos;utilisation, la réalisation de travaux actuariels, la personnalisation des produits et
          services, ainsi que l&apos;évaluation des risques ou l&apos;analyse des coûts et frais en lien avec nos
          Services. Nous pouvons divulguer des données anonymisées ou agrégées à nos sociétés affiliées à l&apos;échelle
          mondiale ainsi qu&apos;à d&apos;autres tiers. Le présent Avis ne limite en rien l&apos;utilisation ou la
          divulgation, par Global Payments, de toute information anonymisée ou agrégée.{' '}
        </TermsPlainText>
        <TermsPlainText>
          Lorsque nous conservons ou utilisons des renseignements désidentifiés, nous continuerons à les conserver et à
          les utiliser uniquement sous une forme désidentifiée et nous nous abstiendrons de tenter de réidentifier ces
          renseignements.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="sourcesOfPI">Sources des renseignements personnels</span>
        </TermsSubTitle>
        <TermsPlainText>
          <b>Renseignements que vous nous fournissez</b>: Nous recueillons les renseignements personnels que vous nous
          fournissez lorsque vous vous inscrivez à un ou plusieurs Services ou lorsque vous communiquez avec nous. Par
          exemple, si vous vous inscrivez à l&apos;un des Services, nous pourrons vous demander votre nom, votre numéro
          de téléphone et/ou votre adresse courriel. Le fait de nous fournir ces renseignements personnels est
          volontaire, et vous pouvez toujours choisir de ne pas fournir certaines informations, mais dans ce cas, vous
          pourriez ne pas être en mesure de bénéficier de certains Services ou d&apos;y participer.
        </TermsPlainText>
        <TermsPlainText>
          <b>Renseignements recueillis auprès de tiers</b>: Nous pouvons recueillir des renseignements à votre sujet
          auprès de tiers dans le cadre de la prestation des Services. Par exemple, nous pouvons recueillir des
          renseignements d&apos;identification de base et des renseignements commerciaux auprès des Marchands afin de
          fournir les Services.
        </TermsPlainText>
        <TermsPlainText>
          <b>Renseignements recueillis par le biais de la technologie</b>: Lorsque vous interagissez avec un courriel
          que nous vous envoyons dans le cadre d&apos;une Campagne de marketing par courriel, nous pouvons recueillir
          automatiquement certains renseignements, tels que l&apos;identifiant de votre compte ou de votre appareil,
          ainsi que des renseignements sur votre utilisation, notamment les liens sur lesquels vous cliquez, les types
          de contenu avec lesquels vous interagissez, la fréquence et la durée de vos activités, ainsi que d&apos;autres
          renseignements concernant votre utilisation des Services.
        </TermsPlainText>
        <TermsPlainText>
          Vous avez la possibilité d&apos;exprimer vos préférences concernant certaines méthodes de collecte
          d&apos;informations par la technologie dans certains Services (voir la section «Témoins (cookies) et autres
          technologies de suivi» pour plus d&apos;informations).
        </TermsPlainText>

        <TermsSubTitle>
          <span id="howUsePI">Comment nous utilisons les renseignements personnels que nous recueillons</span>
        </TermsSubTitle>
        <TermsPlainText>
          Sous réserve de votre consentement lorsque requis par la loi, nous pouvons utiliser les renseignements
          personnels vous concernant aux fins commerciales et d&apos;affaires suivantes:
        </TermsPlainText>
        <ul>
          <li>Pour offrir les Services pour le compte de nos marchands;</li>
          <li>Pour vous contacter par courriel si vous vous êtes abonné à une campagne de marketing par courriel;</li>
          <li>Pour suivre la participation à un programme de fidélité;</li>
          <li>Pour offrir du soutien à la clientèle;</li>
          <li>Pour mener nos activités et à d&apos;autres fins commerciales générales;</li>
          <li>
            Pour vérifier ou maintenir la qualité et la sécurité de nos produits et services, ainsi que pour réparer,
            améliorer, mettre à niveau ou bonifier nos services;
          </li>
          <li>
            Pour détecter les incidents de sécurité, se protéger contre les activités malveillantes, trompeuses,
            frauduleuses ou illégales, et poursuivre les personnes responsables de ces activités;
          </li>
          <li>
            Pour effectuer des vérifications et permettre la tenue de dossiers internes ainsi que l&apos;administration
            des documents;
          </li>
          <li>Pour personnaliser votre expérience lorsque vous utilisez les Services;</li>
          <li>
            Pour suivre les informations concernant les interactions avec vous, y compris, sans s&apos;y limiter, le
            comptage des impressions publicitaires auprès des visiteurs uniques et la vérification du positionnement et
            de la qualité des impressions publicitaires;
          </li>
          <li>Pour l&apos;analyse statistique; et</li>
          <li>
            Pour assurer le respect des lois et/ou règlements applicables et selon ce qui est autrement exigé ou permis
            par les lois et/ou règlements applicables.
          </li>
        </ul>

        <TermsSubTitle>
          <span id="disclosePI">Comment nous divulguons les renseignements personnels que nous recueillons</span>
        </TermsSubTitle>
        <TermsPlainText>
          Nous sommes une entreprise multinationale dont le siège social est situé aux États-Unis, avec des filiales et
          sociétés affiliées dans divers pays à travers le monde. Nous pouvons divulguer des renseignements personnels
          vous concernant à des destinataires situés hors de votre pays, y compris à nos filiales et sociétés affiliées
          à l&apos;échelle mondiale (voir la section «Notre relation avec les sociétés affiliées» ci-dessous), tel que
          décrit dans le présent Avis.
        </TermsPlainText>
        <TermsPlainText>
          Pour chaque catégorie de renseignements personnels que nous recueillons, nous pouvons divulguer ces
          renseignements de la manière et aux fins décrites ci-dessous:
        </TermsPlainText>
        <ul>
          <li>
            Avec des entreprises engagées pour nous aider à gérer nos activités, sous réserve de votre consentement
            lorsque requis par la loi. Ces entreprises peuvent inclure nos sociétés affiliées, des fournisseurs de
            services retenus pour accomplir des fonctions en notre nom ou pour nous fournir des services, notamment
            (sans s&apos;y limiter) des prestataires de services juridiques, comptables, d&apos;audit, de consultation,
            technologiques, de sécurité et autres services professionnels, ainsi que des fournisseurs de services liés à
            nos activités. Certaines parties des Services peuvent être fournies par des organisations avec lesquelles
            Global Payments a une relation contractuelle, y compris des sous-traitants; par conséquent, vos
            renseignements personnels peuvent leur être communiqués;
          </li>
          <li>Avec des tiers avec lesquels vous avez une relation, tels que les Marchands;</li>
          <li>Avec des tiers dans le cadre de promotions, si vous participez à un concours ou à une promotion;</li>
          <li>Avec d&apos;autres tiers auxquels vous nous demandez de divulguer des renseignements personnels;</li>
          <li>
            Avec les organismes de réglementation, y compris les agences étatiques et fédérales, ainsi que d&apos;autres
            parties requises pour assurer la conformité aux lois, règlements et normes industrielles;
          </li>
          <li>
            En réponse à une ordonnance judiciaire ou à une demande de coopération d&apos;un organisme de
            réglementation, d&apos;application de la loi ou autre agence gouvernementale; pour établir ou exercer nos
            droits légaux; pour défendre des réclamations juridiques; ou dans les autres cas requis ou permis par les
            lois et/ou règlements applicables;
          </li>
          <li>
            Lorsque nous estimons que la divulgation est appropriée dans le cadre d&apos;efforts visant à enquêter,
            prévenir ou agir concernant des activités illégales réelles ou soupçonnées, de la fraude ou d&apos;autres
            actes répréhensibles; pour protéger et défendre les droits, la propriété ou la sécurité de Global Payments,
            de ses clients, employés, fournisseurs, affiliés ou autres
          </li>
          <li>
            À des tiers dans le cadre de toute réorganisation, fusion, vente, coentreprise, cession, transfert ou autre
            disposition proposée ou effective de tout ou partie de nos actifs ou actions (y compris dans le cadre de
            procédures de faillite ou similaires).
          </li>
        </ul>

        <TermsSubTitle>
          <span id="affiliateCompanies">Notre relation avec les sociétés affiliées</span>
        </TermsSubTitle>
        <TermsPlainText>
          Sous réserve de votre consentement lorsque requis par la loi applicable, nous pouvons désigner une société
          affiliée pour traiter des renseignements personnels en tant que fournisseur de services. Dans la plupart des
          cas, nous ou le Marchand demeurerons responsables du traitement des renseignements personnels par cette
          société conformément aux lois applicables en matière de protection des données. Puisque nos sociétés affiliées
          sont situées partout dans le monde, les renseignements personnels vous concernant peuvent être transférés et
          stockés aux États-Unis ou dans un autre pays en dehors de votre pays de résidence, lesquels peuvent être
          soumis à des normes de protection des données différentes de celles de votre pays de résidence. Nous prenons
          les mesures appropriées pour transférer les renseignements personnels conformément à la loi applicable.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="financial">Avis concernant les incitatifs financiers / Programme de fidélité légitime</span>
        </TermsSubTitle>
        <TermsPlainText>
          L&apos;un des Services que nous offrons pour le compte de nos Marchands est la possibilité de créer des
          programmes de fidélité. Les Programmes de fidélité peuvent offrir certains avantages, tels que des récompenses
          et des offres exclusives. Ils peuvent également inclure des campagnes promotionnelles seules ou en partenariat
          avec des tiers. Il est important de noter que les <a href={termsUrl}>modalités du Programme de fidélité</a>{' '}
          sont établies par le Marchand. Lorsque vous vous inscrivez à un Programme de fidélité auprès d&apos;un de nos
          Marchands, nous demandons généralement vos coordonnées (par exemple, votre numéro de téléphone). Puisque les
          Programmes de fidélité impliquent la collecte de renseignements personnels, ils peuvent être considérés comme
          un «incitatif financier» selon la législation californienne. La valeur de vos renseignements personnels est
          déterminée par le Marchand en fonction de la valeur des produits, services ou autres avantages gratuits ou à
          prix réduit offerts dans le cadre du Programme de fidélité, moins les coûts associés à la fourniture de ces
          produits, services ou avantages aux participants au Programme.
        </TermsPlainText>
        <TermsPlainText>
          Vous pouvez choisir de vous inscrire à un Programme de fidélité en fournissant votre numéro de téléphone
          lorsqu&apos;il vous est demandé sur un système PDV chez un Marchand participant, puis en suivant les étapes
          d&apos;enregistrement envoyées par SMS par la suite. Une fonctionnalité liée à la carte de paiement peut
          également être utilisée pour offrir un Programme de fidélité au nom des Marchands. Vous pouvez retirer votre
          participation ou vous désinscrire d&apos;un Programme de fidélité en cliquant sur le lien «Désinscription du
          programme de fidélité» dans le(s) message(s) SMS ou en contactant directement le Marchand, comme décrit plus
          en détail dans la section «Gestion de vos préférences» ci-dessous. Si vous choisissez d&apos;exercer votre
          droit de suppression de vos données personnelles (voir la section «Vos droits légaux» ci-dessous), cette
          demande peut rendre impossible votre participation au Programme de fidélité ou la réception des avantages qui
          y sont associés.
        </TermsPlainText>
        <TermsPlainText>
          Veuillez vous référer aux sections «Renseignements personnels que nous recueillons» et «Comment nous utilisons
          les renseignements personnels que nous recueillons» ci-dessus pour plus de détails sur les catégories de
          données personnelles que nous obtenons et les tiers auxquels nous pouvons divulguer ces données en lien avec
          un Programme de fidélité.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="managingPreferences">Gestion de vos préférences</span>
        </TermsSubTitle>
        <TermsPlainText>
          Si vous vous inscrivez au Programme de fidélité d&apos;un Marchand en fournissant votre numéro de téléphone,
          nous pouvons utiliser ces renseignements personnels pour vous envoyer un message SMS afin de compléter le
          processus d&apos;inscription. Le message SMS original contiendra un lien permettant de vous «désinscrire du
          Programme de fidélité». Vous pouvez suivre ce lien pour vous désinscrire du Programme de fidélité. Vous pouvez
          également vous retirer d&apos;un Programme de fidélité en contactant directement le Marchand.
        </TermsPlainText>
        <TermsPlainText>
          Si vous vous abonnez à une Campagne de marketing par courriel d&apos;un Marchand en fournissant votre adresse
          courriel, nous pouvons utiliser ces renseignements personnels pour vous envoyer des courriels promotionnels
          concernant les produits et services du Marchand. Nous prendrons des mesures pour garantir que tout courriel
          promotionnel envoyé par voie électronique offre un moyen simple d&apos;arrêter les communications,
          conformément à la loi applicable. Vous aurez la possibilité de vous désabonner des communications
          promotionnelles par le biais du Service en cliquant sur un lien de «désabonnement» contenu dans les courriels
          promotionnels que nous vous envoyons. Nous traiterons votre demande dans les plus brefs délais, mais vous
          pourriez continuer à recevoir des courriels promotionnels après avoir soumis une demande de désabonnement,
          jusqu&apos;à ce que celle-ci soit traitée dans nos systèmes. Veuillez noter que si vous choisissez de ne plus
          recevoir de communications marketing, nous pourrions toujours vous envoyer des messages administratifs,
          transactionnels ou d&apos;information, auxquels vous ne pouvez pas vous désabonner.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="legalRights">Your Legal Rights Vos droits légaux</span>
        </TermsSubTitle>
        <TermsPlainText>
          Si vous êtes un Client qui utilise les Services en lien avec un Marchand et que vous avez des questions
          concernant vos droits légaux relatifs aux renseignements personnels recueillis dans le cadre des Services que
          nous fournissons pour le compte du Marchand, veuillez consulter directement le Marchand avec lequel vous avez
          une relation.
        </TermsPlainText>
        <TermsPlainText>
          Sous réserve de certaines exceptions, et dans certains cas selon l&apos;activité de traitement concernée, vous
          pouvez avoir le droit de:
        </TermsPlainText>
        <ul>
          <li>Savoir si nous traitons des renseignements personnels vous concernant;</li>
          <li>Savoir comment les renseignements personnels sont utilisés;</li>
          <li>
            Accéder aux renseignements personnels que nous avons recueillis, en faire la demande et les recevoir dans un
            format portable;
          </li>
          <li>
            Refuser que vos renseignements personnels soient vendus, «partagés» ou (dans certaines circonstances)
            utilisés pour certaines activités de profilage;
          </li>
          <li>Demander la correction des inexactitudes dans vos renseignements personnels;</li>
          <li>Demander la suppression de vos renseignements personnels.</li>
        </ul>
        <TermsPlainText>
          Vous pouvez communiquer avec nous en utilisant les coordonnées fournies dans la section «Nous joindre» du
          présent Avis pour obtenir des informations supplémentaires sur la manière d&apos;exercer vos droits. De plus,
          si vous êtes résident de la Californie, du Colorado ou d&apos;une autre juridiction disposant de lois
          similaires, veuillez consulter la section «Renseignements propres à certaines régions» ci-dessous pour plus de
          détails sur l&apos;exercice de vos droits en matière de vie privée.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="dataSecurity">
            Data Security and Data Retention Sécurité des données et conservation des données
          </span>
        </TermsSubTitle>
        <TermsPlainText>
          Nous mettons en place des mesures administratives, techniques et physiques conçues pour protéger les
          renseignements personnels que vous fournissez via les Services contre la destruction, la perte, la
          modification, l&apos;accès, la divulgation ou l&apos;utilisation accidentels, illégaux ou non autorisés.
        </TermsPlainText>
        <TermsPlainText>
          Toute tentative suspectée de violation de nos politiques et procédures, ou de toute action non autorisée
          impliquant nos systèmes d&apos;information, est considérée comme une activité criminelle potentielle. Tout
          acte malveillant informatique suspecté peut être signalé aux autorités compétentes.
        </TermsPlainText>
        <TermsPlainText>
          Veuillez noter que les communications sur Internet, telles que les courriels, ne sont pas sécurisées. Nous
          nous efforçons de protéger les renseignements personnels que vous nous soumettez via les Services conformément
          à nos obligations en vertu des lois et règlements applicables. Toutefois, nous ne pouvons garantir la sécurité
          des données transmises par le biais des Services.
        </TermsPlainText>
        <TermsPlainText>
          Nous conservons les renseignements personnels que nous recueillons dans le cadre des Services pour des
          périodes variables selon leur nature et leur usage, mais uniquement aussi longtemps que nécessaire pour
          atteindre les finalités commerciales ou juridiques légitimes pour lesquelles ils ont été collectés. Les
          critères utilisés pour déterminer nos périodes de conservation incluent:
        </TermsPlainText>
        <ul>
          <li>
            La durée pendant laquelle nous entretenons une relation continue avec vous et vous fournissons les Services
            pour le compte du (des) Marchand(s), par exemple, tant que vous avez un compte ou continuez d&apos;utiliser
            nos Services, ainsi que la période suivante pendant laquelle nous pouvons avoir un besoin légitime de
            consulter les renseignements personnels pour traiter des problèmes éventuels;
          </li>
          <li>
            L&apos;existence d&apos;une obligation contractuelle à laquelle nous sommes soumis, par exemple, nos
            contrats avec nos Marchands peuvent spécifier une certaine période durant laquelle nous sommes tenus de
            conserver les renseignements pour le compte de nos Marchands;
          </li>
          <li>
            L&apos;existence d&apos;une obligation légale à laquelle nous sommes soumis, par exemple, certaines lois
            nous obligent à conserver les registres de vos transactions pendant une certaine période avant de pouvoir
            les supprimer;
          </li>
          <li>
            Le fait que la conservation soit recommandée au regard de notre position juridique, notamment en ce qui
            concerne les délais de prescription applicables, les litiges ou les enquêtes réglementaires.
          </li>
        </ul>

        <TermsSubTitle>
          <span id="cookies">Témoins (cookies) et autres technologies de suivi</span>
        </TermsSubTitle>
        <TermsPlainText>
          Des témoins (cookies) ou des technologies de suivi similaires peuvent être utilisés dans le cadre des
          Campagnes de marketing par courriel pour nous aider à mémoriser des informations concernant votre utilisation
          des Services, comme votre pays, votre langue et d&apos;autres paramètres. Ils nous permettent également de
          fournir les Services de manière plus efficace. Les témoins et autres technologies de suivi nous permettent
          également d&apos;accomplir diverses autres tâches, expliquées plus en détail ci-dessous.
        </TermsPlainText>
        <TermsPlainText>
          Les témoins et technologies de suivi que nous pouvons utiliser dans les Services, notamment dans le cadre
          d&apos;une Campagne de marketing par courriel, se classent dans les catégories suivantes:
        </TermsPlainText>
        <TermsPlainText>
          <i>Strictement nécessaires:</i> Ces témoins sont essentiels au fonctionnement des Services et ne peuvent pas
          être désactivés dans nos systèmes. Ils sont généralement activés en réponse à des actions que vous effectuez,
          comme remplir des formulaires. Vous pouvez configurer votre navigateur pour bloquer ou vous avertir de ces
          témoins, mais leur blocage pourrait nuire au fonctionnement des Services.
        </TermsPlainText>
        <TermsPlainText>
          <i>Performance:</i> Ces témoins nous permettent de compter les visites et les sources de trafic afin de
          mesurer et d&apos;améliorer la performance des Services. Ils nous aident à savoir quelles pages sont les plus
          ou les moins populaires et à comprendre comment les visiteurs interagissent avec les Services. Toutes les
          informations recueillies par ces témoins sont agrégées. Si vous refusez ces témoins, nous ne saurons pas quand
          vous avez visité ou interagi avec les Services, et nous ne pourrons pas surveiller la performance.
        </TermsPlainText>
        <TermsPlainText>
          <i>Fonctionnalité:</i> Ces témoins permettent aux Services d&apos;offrir des fonctionnalités améliorées et une
          personnalisation. Ils peuvent être placés par nous ou par des fournisseurs tiers dont les services ont été
          intégrés à nos pages. Si vous refusez ces témoins, certains Services pourraient ne pas fonctionner
          correctement.
        </TermsPlainText>
        <TermsPlainText>
          <u>Comment supprimer ou bloquer les témoins et autres technologies de suivi.</u> Nous ne répondons pas
          actuellement aux signaux «Ne pas me suivre» (Do-Not-Track) des navigateurs. Sur certains Services, lorsque
          cela est techniquement possible, nous mettrons à disposition des outils pour vous aider à faire des choix
          concernant les témoins et autres technologies de suivi. La plupart des navigateurs vous permettent de refuser
          automatiquement les témoins ou de choisir d&apos;accepter ou de refuser un ou plusieurs témoins d&apos;un site
          donné. Vous pouvez également consulter http://www.allaboutcookies.org/manage-cookies/index.html. Cependant, si
          vous n&apos;acceptez pas les témoins, vous pourriez rencontrer certains désagréments lors de
          l&apos;utilisation des Services. Vous pourriez aussi ne pas recevoir de publicités pertinentes correspondant à
          vos intérêts et besoins.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="externalLinks">Liens externes</span>
        </TermsSubTitle>
        <TermsPlainText>
          Nous pouvons fournir des liens via nos Services, notamment dans les courriels promotionnels envoyés pour le
          compte des Marchands, vers des sites web ou services tiers qui ne sont pas sous notre contrôle. Nous ne
          cautionnons ni ne garantissons en aucune façon le contenu de ces sites web ou services tiers, ni les produits
          et services qu&apos;ils proposent. Nous ne faisons aucune déclaration concernant votre utilisation de ces
          sites web ou services.
        </TermsPlainText>
        <TermsPlainText>
          Veuillez noter que nous ne sommes pas responsables des pratiques en matière de vie privée des exploitants de
          ces autres sites web ou services. Nous encourageons nos utilisateurs à faire preuve de vigilance
          lorsqu&apos;ils visitent d&apos;autres sites web ou services. Vous devriez lire attentivement tout autre avis
          de confidentialité et d&apos;utilisation des témoins (cookies) applicable avant d&apos;accéder à ces sites web
          ou services tiers.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="children">Enfants</span>
        </TermsSubTitle>
        <TermsPlainText>
          Les Services ne sont pas destinés aux enfants ni conçus pour être utilisés par des enfants. Nous ne
          sollicitons pas et n&apos;acceptons pas sciemment de renseignements personnels provenant de personnes de moins
          de 18 ans. Veuillez ne pas utiliser les Services si vous avez moins de 18 ans.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="changesAndUpdates">Changes and Updates Modifications et mises à jour</span>
        </TermsSubTitle>
        <TermsPlainText>
          Nous nous réservons le droit, à notre seule discrétion, de modifier, mettre à jour ou autrement changer le
          présent Avis. La mention «Dernière mise à jour» en haut du présent Avis indique la date de sa dernière
          révision. Toute modification prendra effet dès la publication de l&apos;Avis révisé sur nos Services.
        </TermsPlainText>

        <TermsSubTitle>
          <span id="contactUs">Nous joindre</span>
        </TermsSubTitle>
        <TermsPlainText>
          Si vous avez des questions concernant le présent Avis ou si vous souhaitez exercer vos droits tels que décrits
          dans cet Avis, vous pouvez soumettre une demande en remplissant ce formulaire ou nous contacter comme suit:
        </TermsPlainText>
        <TermsPlainText>
          <div>Global Payments Inc.</div>
          <div>3550 Lenox Rd NE, Suite 3000</div>
          <div>Atlanta, GA 30326</div>
          <div>
            <a href={'mailto:privacy@globalpay.com'}>privacy@globalpay.com</a>
          </div>
          <div>800-367-2638</div>
        </TermsPlainText>
        <TermsPlainText>
          Si vous faites une demande d&apos;exercice d&apos;un droit relatif à la vie privée, nous vérifierons et
          répondrons à votre demande conformément à la loi applicable, en tenant compte du type et de la sensibilité des
          renseignements personnels concernés par la demande. Nous pouvons refuser de donner suite à votre demande
          lorsqu&apos;une exception s&apos;applique. Pour honorer toute demande d&apos;accès ou de suppression, nous
          vous demanderons de fournir suffisamment d&apos;informations pour vérifier votre identité. Par exemple, nous
          pourrions vous demander des informations associées à votre compte, y compris vos coordonnées ou d&apos;autres
          renseignements d&apos;identification. Si vous souhaitez qu&apos;un représentant fasse une demande en votre
          nom, et si la loi applicable le permet, ce représentant peut exercer ces droits comme indiqué ci-dessus. Nous
          traiterons la demande du représentant conformément à la loi applicable. Dans le cadre de notre processus de
          vérification, nous pourrions demander au représentant de fournir, selon le cas, une preuve de son statut
          d&apos;agent autorisé. De plus, nous pourrions exiger que vous vérifiiez votre identité comme décrit dans
          cette section ou que vous confirmiez avoir donné au représentant la permission de soumettre la demande.
        </TermsPlainText>

        <TermsPlainText></TermsPlainText>
      </TermsPlainTextWrapper>
    </TermsTextWrapper>
  );
};
